import { useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { TransitionGroup } from 'react-transition-group';
import classNames from 'classnames';
import { Typography, Fade, makeStyles } from '@material-ui/core';
import DraggableScroll from '../draggableScroll';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import Tooltip from '../tooltip';
import { usePreviousValue } from '../../../utility/hooks';
import { onScrollIntoView } from '../../../utility/uiUtils';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    display: 'flex',
    height: 56,
  },
  tabsWrapper: {
    height: '100%',
  },
  tabs: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'max-content',
    gridColumnGap: 16,
    height: '100%',
  },
  tabWrapper: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifySelf: 'start',
    width: 'max-content',
    maxWidth: 200,
    position: 'relative',
    userSelect: 'none',
    '&::after': {
      content: '""',
      backgroundColor: 'transparent',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: 4,
      transition: 'background-color .2s ease-in',
    },
  },
  activeTab: {
    '&::after': {
      backgroundColor: primary.black,
    },
  },
  count: {
    flexShrink: 0,
    marginLeft: spacing(0.75),
  },
  infoIcon: {
    marginLeft: spacing(1),
  },
}));

const DraggableTabs = ({
  className = undefined,
  tabs = [],
  activeTab = null,
  isVisible = true,
  onTabSelect,
}) => {
  const classes = useStyles();
  const itemRefs = useRef({});
  const previousActiveTab = usePreviousValue(activeTab);

  useLayoutEffect(() => {
    if (itemRefs?.current[activeTab?.id] && previousActiveTab) {
      onScrollIntoView(
        { current: itemRefs.current[activeTab.id] },
        {
          block: 'nearest',
          inline: 'nearest',
        }
      );
    }
  }, [activeTab, previousActiveTab]);

  return (
    <DraggableScroll className={classNames(classes.root, className)}>
      <div className={classes.tabsWrapper}>
        <TransitionGroup className={classes.tabs}>
          {tabs.map(tab => (
            <Fade key={`tab_${tab.id}`} in={isVisible} unmountOnExit>
              <div
                ref={el => {
                  itemRefs.current[tab.id] = el;
                }}
                className={classNames(classes.tabWrapper, {
                  [classes.activeTab]: activeTab?.id === tab.id,
                })}
                onClick={onTabSelect(tab)}
              >
                <TextBoxWithTooltip variant="h5" text={tab.name} />
                {tab.infoText && (
                  <Tooltip
                    customIconClass={classes.infoIcon}
                    text={tab.infoText}
                  />
                )}
                {tab?.count >= 0 && (
                  <Typography
                    className={classes.count}
                    variant="h5"
                  >{`(${tab.count})`}</Typography>
                )}
              </div>
            </Fade>
          ))}
        </TransitionGroup>
      </div>
    </DraggableScroll>
  );
};

DraggableTabs.propTypes = {
  className: PropTypes.string,
  tabs: PropTypes.arrayOf(PropTypes.object),
  activeTab: PropTypes.object,
  isVisible: PropTypes.bool,
  onTabSelect: PropTypes.func.isRequired,
};

export default DraggableTabs;
